import { useCallback } from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    type BaseSubscriptionType,
    type BillingDataSubscriptionInfo,
    isBaseSubscription,
} from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { sendXrayWithPlatform } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { isVkMusicSubscription } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

// https://docs.google.com/spreadsheets/d/1oxESW-0Ne7WtP7gKejV6CJYSv-Q9ssFDC8MY1_chDT0/edit#gid=1290046406

export enum ESubscriptionModalAction {
    open = 'open',
    close = 'close',
    addSpace = 'add-space',
    openCancelAutopayment = 'cancel-autopay',
    closeCancelAutopayment = 'cancel-autopay-close',
    notCancelAutopayment = 'cancel-autopay-save',
    cancelAutopayment = 'cancel-autopay-true',
    cancelAutopaymentSuccess = 'cancel-autopay-success',
    turnOnAutopayment = 'turnon-autopay',
    // tempexp_18907-next-line
    turnOnAutopaymentWithDiscount = 'turnon-autopay-discount',
    changeCard = 'change-card',
    addAnotherCard = 'change-card-true',
    cancelAnotherCard = 'change-card-false',
    addNewCard = 'add-card-true',
    cancelNewCard = 'add-card-false',
    partnerClick = 'partner-click',
    prolongSubs = 'prolong-sub',
    captchaView = 'cancel-capcha-view',
    captchaSaveSubs = 'cancel-capcha-save',
    captchaCancelSubs = 'cancel-capcha-cancel',
    captchaCancelSubsSuccess = 'cancel-capcha-success',
    captchaCancelSubsFail = 'cancel-capcha-fail',
    cancelAutopaymentRestore = 'cancel-autopay-restore',
    showCancelAutopayment = 'cancel-autopay-show',
    showTrialSubs = 'cancel-show-trial',
    // tempexp_16481-start
    showCancelAutoupload = 'cancel-autopay-lost-show',
    notCancelAutoupload = 'cancel-autopay-lost-save',
    cancelAutouploadCancel = 'cancel-autopay-lost-true',
    showCancel10Tb = 'cancel-autopay-10tb-show',
    notCancel10Tb = 'cancel-autopay-10tb-save',
    cancel10tbCancel = 'cancel-autopay-10tb-true',
    // tempexp_16481-end
    // tempexp_16598-start
    showNoAds = 'cancel-no-ads-show',
    notCancelNoAds = 'cancel-no-ads-save',
    cancelNoAds = 'cancel-no-ads-true',
    // tempexp_16598-end

    vkMusicCancelView = 'cancel-view',
    vkMusicCancelClose = 'cancel-close',
    vkMusicCancelCancel = 'cancel-cancel',
    vkMusicCancelSave = 'cancel-save',

    // tempexp_17096-start
    showPauseModal = 'autopay-pause-show',
    cancelPauseSub = 'autopay-pause-true',
    clickPauseSub = 'autopay-pause-save',
    showPauseSuccessModal = 'autopay-paused-show',
    // tempexp_17096-end

    cancelSaleShow = 'cancel-sale-show',
    cancelSaleGet = 'cancel-sale-get',
    cancelSaleReject = 'cancel-sale-reject',
    cancelSaleClose = 'cancel-sale-close',

    // tempexp_17359-start
    familyEmpty = 'cancel-family-empty',
    familyEmptyAdd = 'cancel-family-add',
    family = 'cancel-family',
    familySave = 'cancel-family-save',
    familyCancel = 'cancel-family-cancel',
    // tempexp_17359-end

    // tempexp_18926-start
    autoloadShow = 'cancel-autoload-show',
    autoloadSave = 'cancel-autoload-save',
    autoloadCancel = 'cancel-autoload-cancel',
    autoloadClose = 'cancel-autoload-close',
    // tempexp_18926-end

    graceProlongShow = 'grace-prolong-show',
    graceProlongClick = 'grace-prolong-click',
}

interface ISubscriptionModalAnalytics {
    action: ESubscriptionModalAction;
    product: string;
    tariff: string;
    start_date: number;
    expired_date: number;
    source: 'touch' | 'desktop';
    type: string;
    partner_name?: string;
    platform: string;
    is_trial?: boolean;
    is_vkmusic?: boolean;
    prefix?: string;
}

export type TSendAnalytics = (action: ESubscriptionModalAction) => void;

export const sendSubscriptionModalAnalytics = ({ action, prefix = 'my-tariff', ...rest }: ISubscriptionModalAnalytics): void => {
    sendDwh({
        eventCategory: ECategoryGa.payment,
        action: `${prefix}-${action}`,
        dwhData: rest,
    });
};

export const useSendSubscriptionModalAnalytics = (
    subscription: (BillingDataSubscriptionInfo & { promoId?: string }) | BaseSubscriptionType
): TSendAnalytics => {
    return useCallback(
        (action: ESubscriptionModalAction, prefix = 'my-tariff') => {
            if (isBaseSubscription(subscription)) {
                return;
            }

            const product = subscription.productId || subscription.promoId || 'None';
            sendSubscriptionModalAnalytics({
                is_trial: subscription.isTrial,
                is_vkmusic: isVkMusicSubscription(subscription),
                prefix,
                action,
                type: subscription.type,
                product,
                tariff: product,
                start_date: subscription?.start || 0,
                expired_date: subscription?.expires || 0,
                partner_name: subscription?.partner || 'None',
                source: IS_PHONE_BROWSER ? 'touch' : 'desktop',
                platform: IS_PHONE_BROWSER ? String(EnvironmentSelectors.getMobileType()).toLowerCase() : 'web',
            });

            sendXrayWithPlatform(['my-tariff', action]);
        },
        [subscription]
    );
};
